<template>
  <div class="sib">
    <div class="sib__card">
      <header class="sib__header">
        <i class="bi bi-inbox-fill sib__header-icon" aria-hidden="true" />
        <div>
          <h2 class="sib__title">
            پیام‌ها
            <span v-if="inbox.unread_count" class="sib__badge">{{ fa(inbox.unread_count) }}</span>
          </h2>
          <p class="sib__subtitle">دعوت اسپیکینگ و رویدادهای دوستی</p>
        </div>
        <button
          v-if="inbox.unread_count"
          type="button"
          class="sib__readall"
          :disabled="busy"
          @click="markAll"
        >
          خوانده شد
        </button>
      </header>

      <p v-if="errorMessage" class="sib__error">
        {{ errorMessage }}
        <button type="button" class="sib__retry" @click="load">تلاش مجدد</button>
      </p>
      <p v-else-if="loading && !hasItems" class="sib__empty">در حال بارگذاری…</p>

      <h3 v-if="inbox.speaking_invites.length" class="sib__section">دعوت اسپیکینگ</h3>
      <ul v-if="inbox.speaking_invites.length" class="sib__list">
        <li v-for="invite in inbox.speaking_invites" :key="'inv-' + invite.id" class="sib__row">
          <div class="sib__main">
            <span class="sib__name">{{ personName(invite.from_user) }}</span>
            <span class="sib__meta">اتاق {{ invite.room_code }}</span>
          </div>
          <div class="sib__actions">
            <button type="button" class="sib__join" :disabled="busy" @click="acceptInvite(invite)">
              ورود
            </button>
            <button type="button" class="sib__remove" :disabled="busy" @click="declineInvite(invite.id)">
              رد
            </button>
          </div>
        </li>
      </ul>

      <h3 class="sib__section">رویدادها</h3>
      <p v-if="!inbox.notifications.length" class="sib__empty">پیامی نیست.</p>
      <ul v-else class="sib__list">
        <li
          v-for="item in inbox.notifications"
          :key="item.id"
          class="sib__row"
          :class="{ 'sib__row--unread': !item.read_at }"
          @click="onNotification(item)"
        >
          <div class="sib__main">
            <span class="sib__name">{{ item.title }}</span>
            <span class="sib__meta">{{ item.body }}</span>
          </div>
          <div v-if="isIncomingFriend(item)" class="sib__actions" @click.stop>
            <button
              type="button"
              class="sib__join"
              :disabled="busy"
              @click="acceptFromPayload(item)"
            >
              پذیرش
            </button>
            <button
              type="button"
              class="sib__remove"
              :disabled="busy"
              @click="declineFromPayload(item)"
            >
              رد
            </button>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import {
  acceptFriendRequest,
  acceptSpeakingInvite,
  declineFriendRequest,
  declineSpeakingInvite,
  getSocialInbox,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/api/social";
import { openSpeakingLobby } from "@/api/speakingRooms";
import { handleError } from "@/services/errorHandler";
import type { PublicUser, SocialInbox, SocialNotification, SpeakingInviteItem } from "@/types";

const POLL_MS = 8000;

const loading = ref(true);
const busy = ref(false);
const errorMessage = ref<string | null>(null);
const handledRequestIds = ref<number[]>([]);
const inbox = reactive<SocialInbox>({
  unread_count: 0,
  notifications: [],
  speaking_invites: [],
});

let pollId: ReturnType<typeof setInterval> | null = null;

const hasItems = computed(
  () => inbox.notifications.length + inbox.speaking_invites.length > 0
);

function fa(value: number): string {
  return String(value).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);
}

function personName(user: PublicUser): string {
  return user.full_name?.trim() || user.username;
}

function payloadId(item: SocialNotification, key: string): number | null {
  const value = item.payload?.[key];
  return typeof value === "number" ? value : null;
}

function isIncomingFriend(item: SocialNotification): boolean {
  const requestId = payloadId(item, "request_id");
  return (
    item.type === "friend_request" &&
    requestId != null &&
    !handledRequestIds.value.includes(requestId)
  );
}

async function load(silent = false) {
  if (!silent) loading.value = true;
  try {
    const data = await getSocialInbox();
    inbox.unread_count = data.unread_count;
    inbox.notifications = data.notifications;
    inbox.speaking_invites = data.speaking_invites;
    errorMessage.value = null;
  } catch (e) {
    if (!silent) errorMessage.value = handleError(e, "SocialInbox.load");
  } finally {
    loading.value = false;
  }
}

async function markAll() {
  busy.value = true;
  try {
    await markAllNotificationsRead();
    await load(true);
  } catch (e) {
    errorMessage.value = handleError(e, "SocialInbox.markAll");
  } finally {
    busy.value = false;
  }
}

async function acceptInvite(invite: SpeakingInviteItem) {
  busy.value = true;
  try {
    const accepted = await acceptSpeakingInvite(invite.id);
    openSpeakingLobby(accepted.room_code, "attendee");
    await load(true);
  } catch (e) {
    errorMessage.value = handleError(e, "SocialInbox.acceptInvite");
  } finally {
    busy.value = false;
  }
}

async function declineInvite(inviteId: number) {
  busy.value = true;
  try {
    await declineSpeakingInvite(inviteId);
    await load(true);
  } catch (e) {
    errorMessage.value = handleError(e, "SocialInbox.declineInvite");
  } finally {
    busy.value = false;
  }
}

async function onNotification(item: SocialNotification) {
  if (isIncomingFriend(item) || item.type === "speaking_invite") return;
  if (!item.read_at) {
    try {
      await markNotificationRead(item.id);
      await load(true);
    } catch {
      /* ignore */
    }
  }
}

async function acceptFromPayload(item: SocialNotification) {
  const requestId = payloadId(item, "request_id");
  if (!requestId) return;
  busy.value = true;
  try {
    await acceptFriendRequest(requestId);
    handledRequestIds.value = [...handledRequestIds.value, requestId];
    await markNotificationRead(item.id).catch(() => undefined);
    await load(true);
  } catch (e) {
    errorMessage.value = handleError(e, "SocialInbox.acceptFriend");
  } finally {
    busy.value = false;
  }
}

async function declineFromPayload(item: SocialNotification) {
  const requestId = payloadId(item, "request_id");
  if (!requestId) return;
  busy.value = true;
  try {
    await declineFriendRequest(requestId);
    handledRequestIds.value = [...handledRequestIds.value, requestId];
    await markNotificationRead(item.id).catch(() => undefined);
    await load(true);
  } catch (e) {
    errorMessage.value = handleError(e, "SocialInbox.declineFriend");
  } finally {
    busy.value = false;
  }
}

onMounted(() => {
  void load();
  pollId = setInterval(() => {
    void load(true);
  }, POLL_MS);
});

onBeforeUnmount(() => {
  if (pollId) clearInterval(pollId);
});
</script>

<style scoped>
.sib {
  width: 100%;
  direction: rtl;
  font-family: Vazirmatn, system-ui, "Segoe UI", Tahoma, sans-serif;
}

.sib__card {
  background: #ffffff;
  border: 1px solid #ece4d9;
  border-radius: 24px;
  padding: 22px 20px 18px;
  box-shadow: 0 4px 24px rgba(76, 55, 42, 0.07);
  color: #4c372a;
}

.sib__header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
}

.sib__header-icon {
  font-size: 1.3rem;
  color: #6d5ef8;
  margin-top: 2px;
}

.sib__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sib__badge {
  min-width: 20px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #6d5ef8;
  color: #fff;
  font-size: 0.72rem;
}

.sib__subtitle {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: #8c8176;
}

.sib__readall {
  margin-right: auto;
  border: none;
  background: none;
  color: #6d5ef8;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
}

.sib__section {
  margin: 10px 0 8px;
  font-size: 0.82rem;
  font-weight: 700;
}

.sib__empty {
  margin: 0;
  font-size: 0.78rem;
  color: #8c8176;
  line-height: 1.7;
}

.sib__error {
  margin: 0 0 8px;
  font-size: 0.78rem;
  color: #b42318;
}

.sib__retry {
  border: none;
  background: none;
  color: #6d5ef8;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
}

.sib__list {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
  background: #fffdf8;
  border: 1px solid #ece4d9;
  border-radius: 14px;
  overflow: hidden;
  max-height: 220px;
  overflow-y: auto;
}

.sib__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #ece4d9;
}

.sib__row:last-child {
  border-bottom: none;
}

.sib__row--unread {
  background: #fbf7fc;
}

.sib__main {
  min-width: 0;
}

.sib__name {
  display: block;
  font-size: 0.84rem;
  font-weight: 700;
}

.sib__meta {
  display: block;
  font-size: 0.72rem;
  color: #8c8176;
  line-height: 1.5;
}

.sib__actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.sib__join,
.sib__remove {
  padding: 6px 12px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
}

.sib__join {
  border: none;
  background: #6d5ef8;
  color: #fff;
}

.sib__remove {
  border: 1px solid #f0c7c2;
  background: #fff5f4;
  color: #b42318;
}

.sib__join:disabled,
.sib__remove:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>
